// Phase 84.M.35 — Phone OTP Modal (Zalo ZNS).
// Reusable inside UpdateProfileModal: opens when user clicks "Xác thực SĐT".
// Flow:
//   1) Sender → POST /api/auth/otp/send {phone}.
//   2) User nhập 6 số → POST /api/auth/otp/verify {phone, otp}.
//   3) Success → onVerified(phone) callback → close.
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, ShieldCheck, ArrowClockwise } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { formatApiErrorDetail } from "../../lib/authApi";

const OTP_LENGTH = 6;

export default function PhoneOtpModal({ open, phone, onClose, onVerified, onPhoneTaken }) {
  const [step, setStep] = useState("send"); // send | verify
  const [otp, setOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [debugCode, setDebugCode] = useState(null); // mock mode only
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep("send");
      setOtp("");
      setSending(false);
      setVerifying(false);
      setCooldown(0);
      setDebugCode(null);
    }
  }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const send = async () => {
    if (!phone || phone.length < 9) {
      toast.error("Vui lòng nhập SĐT trước khi gửi OTP");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/auth/otp/send", { phone });
      toast.success("Đã gửi OTP qua Zalo");
      setStep("verify");
      setCooldown(60);
      if (r.data?.debug_code) {
        setDebugCode(r.data.debug_code);
      }
      setTimeout(() => inputRef.current?.focus(), 200);
    } catch (e) {
      // Phase 85.A.47 — Pre-check 409 phone đã dùng: KHÔNG toast (UX
      // kém), thay vào callback parent để hiển thị inline warning ngay
      // dưới input phone + cho user đổi số nhanh chóng.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "phone_already_used") {
        onPhoneTaken?.(detail);
        onClose?.();
        return;
      }
      toast.error(formatApiErrorDetail(detail) || "Không gửi được OTP");
    } finally {
      setSending(false);
    }
  };

  const verify = async () => {
    if (otp.length !== OTP_LENGTH) {
      toast.error(`Vui lòng nhập đủ ${OTP_LENGTH} số`);
      return;
    }
    setVerifying(true);
    try {
      await api.post("/auth/otp/verify", { phone, otp });
      toast.success("Đã xác thực SĐT");
      onVerified?.(phone);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "OTP không đúng");
    } finally {
      setVerifying(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur" data-testid="phone-otp-modal">
      <div className="w-full sm:max-w-sm bg-[#0B1220] border-t border-white/10 sm:border sm:rounded-3xl rounded-t-3xl shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
          <h3 className="text-[15px] font-black text-white inline-flex items-center gap-1.5">
            <ShieldCheck size={15} weight="bold" className="text-[#FF8A00]" /> Xác thực SĐT
          </h3>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-white/8 flex items-center justify-center" data-testid="phone-otp-close">
            <X size={14} weight="bold" className="text-white/80" />
          </button>
        </div>

        <div className="p-4 space-y-3.5">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-[12.5px] text-white/85">
            Chúng tôi sẽ gửi mã OTP <span className="font-black text-[#FF8A00]">6 số</span> đến số Zalo:
            <div className="mt-1 text-[15px] font-black text-white tabular-nums tracking-wider">{phone}</div>
          </div>

          {step === "send" && (
            <button
              type="button"
              onClick={send}
              disabled={sending}
              className={`w-full h-11 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white font-black text-[13px] tracking-wide disabled:opacity-40 ${
                /* Phase 85.A.176 — Shake + glow attention khi sẵn sàng gửi. */
                !sending ? "otp-send-attention" : ""
              }`}
              data-testid="phone-otp-send-btn"
            >
              {sending ? "Đang gửi…" : "Gửi mã OTP qua Zalo"}
            </button>
          )}

          {step === "verify" && (
            <>
              <label className="block">
                <span className="block text-[10.5px] uppercase tracking-widest text-white/55 font-black mb-1.5">Nhập 6 số OTP</span>
                <input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  maxLength={OTP_LENGTH}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH))}
                  className="w-full h-12 px-4 rounded-xl bg-[#0F1623] border border-white/15 text-white text-[22px] font-black tabular-nums tracking-[6px] text-center outline-none focus:border-[#FF8A00]/60"
                  placeholder="••••••"
                  data-testid="phone-otp-input"
                />
              </label>
              {debugCode && (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[10.5px] text-amber-200 font-mono" data-testid="phone-otp-debug">
                  [MOCK] Code: {debugCode}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={send}
                  disabled={sending || cooldown > 0}
                  className="h-11 rounded-xl bg-white/8 border border-white/15 text-white font-bold text-[12px] inline-flex items-center justify-center gap-1 disabled:opacity-40"
                  data-testid="phone-otp-resend"
                >
                  <ArrowClockwise size={12} weight="bold" />
                  {cooldown > 0 ? `Gửi lại sau ${cooldown}s` : "Gửi lại"}
                </button>
                <button
                  type="button"
                  onClick={verify}
                  disabled={otp.length !== OTP_LENGTH || verifying}
                  className="h-11 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-400 text-[#062014] font-black text-[12.5px] disabled:opacity-40"
                  data-testid="phone-otp-verify-btn"
                >
                  {verifying ? "Đang xác thực…" : "Xác thực"}
                </button>
              </div>
            </>
          )}
          <p className="text-[10px] text-white/40 leading-relaxed">
            Mã OTP có hiệu lực 5 phút. Nếu Zalo chưa kết nối với SĐT, vui lòng kiểm tra ứng dụng Zalo trên máy.
          </p>
        </div>
      </div>
    </div>
  );
}
